import { ProjectOrderedQtyPayload } from '../../Processes/Gateways/CustomProjects/Interfaces/ProjectItemOrderedQtyPayload';
import { ItemEntity } from '@entities/Item/Types';
import { useProjectStore } from '../../Stores/ProjectStore';
import { CustomProjectsGateway } from '../../Processes/Gateways/CustomProjects/CustomProjectsGateway';
import { CustomProjectModel } from '@entities/CustomProject/CustomProjectModel';
import { useItemsSelectionStore } from '../../Stores/ItemsSelectionStore';
import { CartGateway } from '../../Processes/Gateways/Cart/CartGateway';
import { useFragmentsStore } from '../../Stores/FragmentsStore';
import { CreateOrderedQtyPayloadForAllFragments } from '@viewModels/CartViewModel/Services/CreateOrderedQtyPayloadForAllFragments';
import { CreateOrderedQtyPayloadForSelectedItems } from '@viewModels/CartViewModel/Services/CreateOrderedQtyPayloadForSelectedItems';

const projectGateway = new CustomProjectsGateway();
const customProjectModel = new CustomProjectModel();
const cartGateway = new CartGateway();

export class CartViewModel {
    fetchAddToCart = async (itemsPayload: ItemEntity[]) => {
        const { customProject } = useProjectStore.getState();
        const { setSelectedItems } = useItemsSelectionStore.getState();

        const response = await cartGateway.addToCart(
            itemsPayload,
            customProject
        );

        if (response.error) {
            return response;
        }

        setSelectedItems(itemsPayload);
        return response;
    };

    fetchItemOrderedQty = async (
        orderedQtyPayload: ProjectOrderedQtyPayload[],
        items: ItemEntity[]
    ) => {
        const { customProject } = useProjectStore.getState();
        const { setSelectedItems } = useItemsSelectionStore.getState();

        const response = await projectGateway.addItemOrderedQty(
            customProject.id,
            orderedQtyPayload
        );

        if (response.data) {
            customProjectModel.initialize(response.data);
            useProjectStore.setState({
                customProject: customProjectModel,
            });

            setSelectedItems(items);
        }

        return response;
    };

    addAllItemsToCart = async () => {
        const { parsedFragments } = useFragmentsStore.getState();
        const { customProject } = useProjectStore.getState();
        const { setAddAllToCartLoading } = useItemsSelectionStore.getState();

        setAddAllToCartLoading(true);
        try {
            const orderedQtyPayload = CreateOrderedQtyPayloadForAllFragments(
                parsedFragments,
                customProject
            );

            const allItemTypes = parsedFragments
                .flatMap(fragment => fragment.itemTypes)
                .filter(itemType => itemType.qty > 0 && !itemType.labor);
            const allItems = allItemTypes
                .flatMap(item => item.items)
                .filter(Boolean) as ItemEntity[];

            await this.fetchItemOrderedQty(orderedQtyPayload, allItems);
            return await this.fetchAddToCart(allItems);
        } finally {
            setAddAllToCartLoading(false);
        }
    };

    addSelectedItemsToCart = async () => {
        const { parsedFragments } = useFragmentsStore.getState();
        const { customProject } = useProjectStore.getState();
        const { selectedItems, setAddSelectedToCartLoading } =
            useItemsSelectionStore.getState();

        setAddSelectedToCartLoading(true);
        try {
            if (selectedItems.length < 1) {
                return {
                    error: true,
                    message: 'Select at least one product first!',
                };
            }
            const orderedQtyPayload: ProjectOrderedQtyPayload[] =
                CreateOrderedQtyPayloadForSelectedItems(
                    selectedItems,
                    parsedFragments,
                    customProject
                );

            const response = await this.fetchItemOrderedQty(
                orderedQtyPayload,
                selectedItems
            );

            if (!response.error) {
                return await this.fetchAddToCart(selectedItems);
            } else {
                return { error: true, message: response.message };
            }
        } finally {
            setAddSelectedToCartLoading(false);
        }
    };
}
